import { useState } from "react";
import { toast } from "sonner";
import { X, Coins, Loader2, Sparkles, ShoppingCart, Info } from "lucide-react";
import { aiApi } from "@/lib/api";
import { useCredits } from "@/context/CreditsContext";
import { AI } from "@/constants/testIds";

const EXPLAINER_KEY = "promptivo.aiCreditsExplainerSeen";

export function AiActionButton({ actionKey, buildPayload, onResult, label, icon: Icon = Sparkles, className, testId, disabled }) {
  const { balance, refresh, openBuy } = useCredits();
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [phase, setPhase] = useState("confirm"); // confirm | insufficient | running
  const [explainer, setExplainer] = useState(false);

  const start = async () => {
    if (disabled) return;
    try {
      const q = await aiApi.quote(actionKey);
      setQuote(q);
      setPhase(q.sufficient ? "confirm" : "insufficient");
      try {
        setExplainer(!localStorage.getItem(EXPLAINER_KEY));
      } catch {
        setExplainer(false);
      }
      setOpen(true);
    } catch (e) {
      toast.error("Nie udało się pobrać wyceny.");
    }
  };

  const confirm = async () => {
    try {
      localStorage.setItem(EXPLAINER_KEY, "1");
    } catch {
      /* ignore */
    }
    setExplainer(false);
    setPhase("running");
    try {
      const payload = { actionKey, ...(buildPayload ? buildPayload() : {}) };
      const res = await aiApi.run(payload);
      onResult?.(res.output, res);
      await refresh();
      setOpen(false);
      toast.success(`Gotowe. Pobrano ${res.cost} kr.`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && detail?.code === "PAID_OPS_BLOCKED_DEBT") {
        setOpen(false);
        await refresh();
        toast.error(`Masz dług kredytowy (${detail.debt} kr.) po zwrocie płatności. Płatne operacje są wstrzymane — dokup kredyty, aby go wyrównać.`, { duration: 7000 });
        openBuy();
        return;
      }
      if (e.response?.status === 402 && detail?.code === "INSUFFICIENT_CREDITS") {
        setQuote((q) => ({ ...q, sufficient: false, balance: detail.balance, recommendedPack: detail.recommendedPack }));
        setPhase("insufficient");
        return;
      }
      if (e.response?.status === 502) {
        toast.success("Kredyty zwrócone — operacja nie powiodła się. Spróbuj ponownie.");
        await refresh();
        setOpen(false);
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Operacja nie powiodła się.");
      setPhase("confirm");
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={start}
        disabled={disabled}
        className={className || "inline-flex items-center gap-1.5 rounded-field border border-brand-line px-3 py-1.5 text-xs font-medium text-brand-text transition-colors hover:border-brand-accent/60 disabled:opacity-50"}
      >
        <Icon size={14} strokeWidth={2} /> {label}
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center" onClick={() => phase !== "running" && setOpen(false)} data-testid={AI.modal}>
          <div className="w-full max-w-md rounded-t-panel border border-brand-line bg-brand-surface p-6 sm:rounded-panel" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-brand-text">
                {phase === "insufficient" ? "Za mało kredytów" : quote?.label || label}
              </h2>
              {phase !== "running" && (
                <button type="button" onClick={() => setOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-field text-brand-sec hover:bg-brand-elevated">
                  <X size={17} />
                </button>
              )}
            </div>

            {explainer && phase === "confirm" && (
              <div className="mb-4 flex items-start gap-2 rounded-field bg-brand-elevated px-3 py-2.5 text-xs text-brand-sec">
                <Info size={14} className="mt-0.5 shrink-0 text-brand-accent" />
                <span>Funkcje AI kosztują kredyty. Płacisz tylko za wykonaną operację — przy błędzie kredyty wracają. Przeglądanie katalogu jest bezpłatne; kredyty wykorzystujesz na przygotowanie promptu i dodatkowe funkcje.</span>
              </div>
            )}

            {phase === "insufficient" ? (
              <div data-testid={AI.insufficient}>
                <p className="text-sm text-brand-sec">
                  Ta operacja kosztuje <strong className="text-brand-text">{quote?.cost} kr.</strong>, a Twoje saldo to{" "}
                  <strong className="text-brand-text">{quote?.balance ?? balance} kr.</strong>
                </p>
                {quote?.recommendedPack && (
                  <div className="mt-4 rounded-field border border-brand-line bg-brand-bg p-4">
                    <p className="text-xs uppercase tracking-wide text-brand-muted">Wystarczy najmniejszy pakiet</p>
                    <p className="mt-1 font-display text-base font-semibold text-brand-text">
                      {quote.recommendedPack.name} — {quote.recommendedPack.creditsTotal} kr. za {quote.recommendedPack.grossPrice} {quote.recommendedPack.currency}
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  data-testid={AI.topupButton}
                  onClick={() => { setOpen(false); openBuy(); }}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on"
                >
                  <ShoppingCart size={16} /> Dokup kredyty
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-2 rounded-field border border-brand-line bg-brand-bg p-4 text-sm">
                  <Row label="Operacja" value={quote?.label} />
                  <Row label="Koszt" value={<span className="inline-flex items-center gap-1 text-brand-accent"><Coins size={13} /> {quote?.cost} kr.</span>} />
                  <Row label="Saldo teraz" value={`${quote?.balance} kr.`} />
                  <Row label="Saldo po wykonaniu" value={`${quote?.balanceAfter} kr.`} bold />
                </div>
                <button
                  type="button"
                  data-testid={AI.runButton}
                  onClick={confirm}
                  disabled={phase === "running"}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {phase === "running" ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={16} />}
                  {phase === "running" ? "Generuję…" : "Uruchom"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-brand-sec">{label}</span>
      <span className={bold ? "font-semibold text-brand-text" : "text-brand-text"}>{value}</span>
    </div>
  );
}
